import React, { MouseEventHandler, ReactNode, useState, useImperativeHandle, forwardRef } from "react";
import IconButton from "./IconButton";
import SVG from "src/utils/SVG";
import Button from "./Button";
import CheckBox from "./CheckBox";
import ModalPortal from "src/modalPortal";
import styled from "styled-components";

interface IImageModalProps {
  children: ReactNode;
  className?: string;
  onChange?: (e: boolean) => void;
}
export interface RefProps {
  modalOpen: () => void;
  modalClose: () => void;
}

const ImageModal = (props: IImageModalProps, ref: React.Ref<RefProps>) => {
  const [visible, setVisible] = useState(false);

  const onModalOpen = () => {
    setVisible(true);
    // 모달창 열 때 바디 스크롤 잠금
    document.body.style.overflow = "hidden";
  };

  const onModalClose = () => {
    // 모달창 닫을 때 바디 스크롤 풀림
    document.body.style.overflow = "unset";
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    modalOpen: () => onModalOpen(),
    modalClose: () => onModalClose(),
  }));

  return (
    <ModalPortal>
      <ModalOverlay className={visible ? "open-modal" : ""}>
        <ModalWrapper>
          <ModalHeader>
            <IconButton btnName="팝업닫기" onClick={onModalClose}>
              <SVG name="imageModalClose" width={24} height={24} viewBox="0 0 24 24" />
            </IconButton>
          </ModalHeader>
          <ModalBody>
            <div>{props.children}</div>
          </ModalBody>
        </ModalWrapper>
      </ModalOverlay>
    </ModalPortal>
  );
};

const ModalOverlay = styled.div`
  z-index: 2000;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: none;
  &.open-modal {
    ${({ theme }) => theme.flexBox.flexMixin("column", "flex-end")};
  }
`;
const ModalWrapper = styled.div`
  z-index: 2001;
  position: relative;
  overflow: hidden;
  display: block;
  width: 100%;
  height: 100%;
  /* max-height: calc(100% - ${({ theme }) => theme.mobile.notchHeight}px); */
  background-color: #fff;

  @media (min-width: 769px) {
    background-color: #000;
  }
`;

const ModalHeader = styled.div`
  z-index: 2002;
  position: fixed;
  top: calc(0px + ${({ theme }) => theme.mobile.notchHeight}px);
  left: 0;
  right: 0;
  padding: 18px 15px 0 15px;
  height: 55px;
  > button {
    position: absolute;
    top: 18px;
    right: 15px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.2);

    ${/* VMD-402: 확대 후 X 가 안 먹던 원인.
     * index.html 뷰포트가 maximum-scale=2 라 더블탭 확대가 켜져 있고, touch-action 이 auto 면
     * iOS 가 두 번째 탭을 기다리느라 탭을 바로 넘기지 않는다. 핀치 확대 직후에는 그렇게 미뤄진
     * click 이 삼켜져서 닫히지 않는다. manipulation 은 더블탭 확대만 끄고 핀치 확대는 남긴다.
     * VMD-390 이 notice/GalleryImageModal 에 넣은 것과 같은 수정이다. */ ""}
    touch-action: manipulation;

    ${/* 보이는 박스는 24x24 그대로 두고 눌리는 영역만 44x44 로 넓힌다(iOS 권장 최소 터치 타깃). */ ""}
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 44px;
      height: 44px;
      transform: translate(-50%, -50%);
    }
  }

  @media (min-width: 769px) {
    > button {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const ModalBody = styled.div`
  overflow: hidden;
  overflow-y: auto;
  height: 100%;
  > div {
    height: 100%;
  }
`;

export default forwardRef(ImageModal);
